import { useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { api } from '../api';
import { findObjectType } from '../objectTypes';

export function CreatePage() {
  const { type } = useParams<{ type: string }>();
  const [, setLocation] = useLocation();
  const meta = findObjectType(type);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!meta) {
    return <div className="p-8">Unknown object type</div>;
  }

  async function create() {
    setError(null);
    setSaving(true);
    // Strip empty strings — the API treats missing keys as "not set"
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      if (v !== '') payload[k] = v;
    }
    try {
      const record = await api.create(type, payload);
      setLocation(`/objects/${type}/${record.id}`);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <div className="p-8 max-w-2xl">
      <div className="mb-4">
        <Link
          href={`/objects/${type}`}
          className="text-sm text-text-muted hover:text-accent"
        >
          ← {meta.label_plural}
        </Link>
      </div>
      <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
        <span>{meta.icon}</span> New {meta.label}
      </h1>
      <p className="text-text-muted text-sm mb-6">
        Required fields show on save if missing.
      </p>

      {error && (
        <div className="card p-4 mb-4 border-danger text-danger text-sm">
          {error}
        </div>
      )}

      <div className="card p-5">
        <div className="grid grid-cols-2 gap-4">
          {meta.display_fields.map((f) => (
            <div key={f}>
              <label className="label">{prettify(f)}</label>
              <input
                className="input"
                value={values[f] ?? ''}
                onChange={(e) => setValues({ ...values, [f]: e.target.value })}
                placeholder={f === 'email' ? 'jane@example.com' : ''}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Link href={`/objects/${type}`} className="btn">
            Cancel
          </Link>
          <button onClick={create} disabled={saving} className="btn-primary">
            {saving ? 'Creating…' : `Create ${meta.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function prettify(field: string): string {
  return field
    .replace(/^hs_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
